import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { deleteApplication } from '../../services/applicationService';
import { API_URL } from '../../config';
import { mockAuthHeaders } from '../../utils/mockAuth';

/**
 * ApplicationHeader - Always-visible header section
 * 
 * Shows:
 * - Job title and company
 * - Application status
 * - Quick actions (refresh, delete, export)
 */
const ApplicationHeader = ({ application, jobDescription, onUpdate, onRefresh }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const menuOpen = Boolean(anchorEl);
  const statusMenuOpen = Boolean(statusMenuAnchor);

  // Status options
  const statusOptions = [
    'Unsubmitted',
    'Submitted',
    'Denied',
    'Scheduling Interview',
    'Interview Scheduled',
    'Interview Complete'
  ];

  // Get status color
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'submitted') return 'primary';
    if (statusLower === 'denied') return 'error';
    if (statusLower.includes('interview')) return 'success';
    return 'default';
  };

  // Handle status change
  const handleStatusChange = (newStatus) => {
    onUpdate({ status: newStatus });
    setStatusMenuAnchor(null);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this application? This cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await deleteApplication(application.id);
      navigate('/applications');
    } catch (err) {
      console.error('Error deleting application:', err);
      alert('Failed to delete application');
      setDeleting(false);
    }
  };

  // Handle export PDF
  const handleExportPDF = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/applications/${application.id}/resume/pdf`,
        {
          headers: { ...mockAuthHeaders() },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${application.job_description?.company || 'resume'}_${application.job_description?.position || 'position'}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('No resume PDF available yet. Build your resume first.');
      }
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert('Failed to export PDF');
    }
    setAnchorEl(null);
  };

  // Get position/company from structured_data with fallback chain
  const structuredData = jobDescription?.structured_data || application?.job_description || {};
  const position = structuredData.position || application?.position || 'Unknown Position';
  const company = structuredData.company || application?.company || 'Unknown Company';
  const location = structuredData.location || application?.location || '';
  const status = application?.status || 'Unsubmitted';

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        {/* Left: Title and Company */}
        <Box flex={1}>
          <Typography variant="h4" component="h1" gutterBottom>
            {position}
          </Typography>
          
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center">
              <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="h6" color="text.secondary">
                {company}
              </Typography>
            </Box>
            
            {location && (
              <Box display="flex" alignItems="center">
                <LocationIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary">
                  {location}
                </Typography>
              </Box>
            )}
          </Stack>

          <Box display="flex" gap={1} alignItems="center">
            {/* Status Chip - Clickable to change */}
            <Chip
              label={status}
              color={getStatusColor(status)}
              onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
              sx={{ cursor: 'pointer' }}
            />
            
            {/* Application Date */}
            {application.application_date && (
              <Typography variant="caption" color="text.secondary">
                Created: {new Date(application.application_date).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: Actions */}
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="More actions">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={statusMenuOpen}
        onClose={() => setStatusMenuAnchor(null)}
      >
        <MenuItem disabled sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
          Change Status
        </MenuItem>
        <Divider />
        {statusOptions.map((statusOption) => (
          <MenuItem
            key={statusOption}
            onClick={() => handleStatusChange(statusOption)}
            selected={statusOption === status}
          >
            <Chip
              label={statusOption}
              color={getStatusColor(statusOption)}
              size="small"
              sx={{ mr: 1 }}
            />
            {statusOption}
          </MenuItem>
        ))}
      </Menu>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={handleExportPDF}>
          <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
          Export Resume PDF
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDelete} disabled={deleting} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete Application
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default ApplicationHeader;
